import { css } from '@emotion/css';
import { useGetStory } from '@src/apis/queries';
import DagooCoffeeIcon from '@src/assets/dagoo-coffee.svg';
import { LoadingSpinner } from '@src/components/LoadingSpinner';
import { ProjectItem } from '@src/components/ProjectItem';
import { TopNavigation } from '@src/components/TopNavigationBar';
import { usePostMessage } from '@src/hooks/usePostMessage';
import { Story } from '@src/models/story';
import { useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useParams, useSearchParams } from 'react-router-dom';

export const StoryPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isClose = searchParams.get('isClose') === 'false' ? false : true;
  const { data: storyData, isLoading } = useGetStory(id);
  const [story, setStory] = useState<Story>({} as Story);

  useEffect(() => {
    if (storyData?.data) {
      setStory(storyData.data as Story);
    }
  }, [storyData]);

  const renderProjects = () => {
    if (story.projects && story.projects.length > 0) {
      return story.projects.map((proj, index) => <ProjectItem key={index} story={story} project={proj} />);
    }
    if (story.projects && story.projects.length === 0) {
      return (
        <div className={EmptyProjectsStyle}>
          <img src={DagooCoffeeIcon} alt='empty' />
          <div className={EmptyTextWrapStyle}>
            <p className={EmptyTitleStyle}>콘텐츠를 준비하고 있어요</p>
            <p className={EmptyDescStyle}>
              현재 플레이 가능한 콘텐츠가 없어요.
              <br />
              다음에 다시 방문해 주세요!
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const postMessageToParent = usePostMessage();
  const sendMessageToParent = () => {
    const closeMessage: PostMessageType<ActionMessage> = {
      type: 'action',
      data: {
        messageDataType: 'close',
        payload: { status: true },
      },
    };

    if (window.isFlutter()) postMessageToParent<ActionMessage>(closeMessage);
    else history.back();
  };

  return (
    <>
      <TopNavigation
        title={story.title}
        isClose={isClose}
        onClickClose={sendMessageToParent}
        onClickBack={sendMessageToParent}
      />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className={StoryPageStyle}>
          {story.type === 'ImageHeader' && (
            <div className={StoryContentStyle}>
              <Img loader={<LoadingSpinner />} src={story.content!} alt={story.id} />
            </div>
          )}
          <div className={StoryProjectsStyle}>{renderProjects()}</div>
        </div>
      )}
    </>
  );
};

const StoryPageStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding-top: 48px;
  padding-bottom: 65px;
  gap: 12px;
`;

const StoryContentStyle = css`
  align-items: center;
  > img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const StoryProjectsStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 8px;
  padding: 0 16px;
`;

const EmptyProjectsStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const EmptyTextWrapStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyTitleStyle = css`
  color: var(--text-text-default, #1a1a1a);
  text-align: center;
  font-size: var(--font-size-font-size-4, 18px);
  font-style: normal;
  font-weight: var(--font-weight-font-weight-extrabold, 800);
  line-height: var(--line-height-font-height-3, 26px);
`;

const EmptyDescStyle = css`
  color: var(--text-text-caption, #8f8f8f);
  text-align: center;
  font-size: var(--font-size-font-size-6, 14px);
  font-style: normal;
  font-weight: var(--font-weight-font-weight-regular, 400);
  line-height: var(--line-height-font-height-5, 20px);
  letter-spacing: -0.1px;
`;
